import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, Navigate, useParams } from 'react-router-dom';

export const COMPARISONS = {
    jobscan: {
        competitor: 'Jobscan',
        title: 'CV Analyse vs Jobscan',
        description:
            'Jobscan compare un CV anglais à une offre (ATS US). CV Analyse note un CV français sur 100, sans coller d’offre, avec un essai sans carte.',
        intro:
            'Jobscan est un outil américain conçu pour coller un CV à une fiche de poste et mesurer le recouvrement de mots-clés ATS. CV Analyse sert un autre usage : noter un CV francophone, expliquer les faiblesses par section, rester lisible par un recruteur français.',
        rows: [
            ['Langue et marché', 'Français, RGPD, recruteurs FR', 'Anglais, marché US / international'],
            ['Entrée', 'CV seul (PDF ou DOCX) ou CV + offre', 'CV + offre d’emploi obligatoire pour le matching'],
            ['Résultat', 'Score /100, 5 critères, suggestions', 'Pourcentage de match ATS et mots-clés'],
            ['Prix', 'Aperçu offert, rapport + CV dès 5 €, sans abo', 'Abonnement mensuel (offre US)'],
            ['Design CV', 'Déconseille colonnes / Canva image', 'Peut analyser un fichier, focus mots-clés'],
        ],
        betterWhen: 'Vous postulez en France, vous voulez un diagnostic même sans offre sous la main, et vous refusez un abonnement US.',
        betterThem: 'Vous visez surtout des ATS américains et vous avez déjà une offre d’emploi précise à coller.',
    },
    'resume-worded': {
        competitor: 'Resume Worded',
        title: 'CV Analyse vs Resume Worded',
        description:
            'Resume Worded score des CV et profils LinkedIn en anglais. CV Analyse est un outil français : score, ATS, crédits à la demande.',
        intro:
            'Resume Worded s’est imposé sur le scoring de CV et de profils LinkedIn en anglais, avec une logique « instant score » et des templates. CV Analyse ne prétend pas remplacer LinkedIn : il note le fichier que vous envoyez aux recruteurs français.',
        rows: [
            ['Langue', 'Français uniquement dans le produit', 'Anglais (US / UK)'],
            ['Périmètre', 'Fichier CV (PDF, DOCX)', 'CV + scoring LinkedIn'],
            ['Critères', 'Structure, clarté, mots-clés, secteur, chiffres', 'Score instantané + suggestions de formulation EN'],
            ['Données', 'Hébergement EU, suppression de compte', 'Produit US, CGU américaines'],
            ['Tarif', 'Aperçu offert, rapport + CV dès 5 €', 'Abonnement / crédits selon l’offre en vigueur'],
        ],
        betterWhen: 'Votre recherche d’emploi est en France et votre CV est en français.',
        betterThem: 'Vous postulez à l’international en anglais et vous voulez aussi un audit LinkedIn.',
    },
    canva: {
        competitor: 'Canva',
        title: 'CV Analyse vs Canva',
        description:
            'Canva sert à mettre en page un CV. CV Analyse sert à le noter. Un joli PDF image passe souvent mal dans un ATS.',
        intro:
            'Canva est un outil de design. Ses modèles de CV sont beaux à l’écran, souvent en colonnes, icônes et fond. Un ATS et beaucoup de recruteurs lisent le texte, pas la maquette. CV Analyse lit le texte extractible et dit ce qui bloque.',
        rows: [
            ['Rôle', 'Analyser et conseiller', 'Mettre en page / illustrer'],
            ['ATS', 'Pénalise l’absence de texte extractible', 'Les modèles visuels cassent souvent le parsing'],
            ['Formats', 'PDF texte, DOCX', 'Export PDF/PNG, parfois sans calque texte'],
            ['Score', 'Oui, /100 et 5 critères', 'Non'],
            ['Quand l’utiliser', 'Avant d’envoyer le fichier', 'Pour un portfolio visuel, pas pour un ATS'],
        ],
        betterWhen: 'Vous voulez savoir si le fichier est lisible par un recruteur et par un ATS.',
        betterThem: 'Vous préparez un one-page visuel pour un métier créatif, hors circuit ATS.',
    },
};

const Vs = () => {
    const { slug } = useParams();
    const page = COMPARISONS[slug];
    if (!page) return <Navigate to="/comparatifs" replace />;

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: page.title,
        description: page.description,
        url: `https://www.cv-analyse.fr/vs/${slug}`,
        inLanguage: 'fr-FR',
        about: [{ '@type': 'SoftwareApplication', name: 'CV Analyse' }, { '@type': 'SoftwareApplication', name: page.competitor }],
    };

    return (
        <div className="min-h-screen bg-slate-200 pt-16">
            <Helmet>
                <title>{page.title} — comparatif</title>
                <meta name="description" content={page.description} />
                <link rel="canonical" href={`https://www.cv-analyse.fr/vs/${slug}`} />
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Helmet>

            <div className="bg-slate-900 text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
                    <p className="text-sm text-slate-400 mb-3">
                        <Link to="/comparatifs" className="hover:text-white">Comparatifs</Link>
                        {' / '}
                        {page.competitor}
                    </p>
                    <h1 className="text-3xl md:text-4xl font-semibold mb-3">{page.title}</h1>
                    <p className="text-slate-300 leading-relaxed">{page.intro}</p>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                <section className="card-surface p-6 sm:p-8 overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 text-slate-500">
                                <th className="py-2 pr-3 font-medium">Critère</th>
                                <th className="py-2 pr-3 font-medium">CV Analyse</th>
                                <th className="py-2 font-medium">{page.competitor}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {page.rows.map((row) => (
                                <tr key={row[0]} className="border-b border-slate-100 align-top">
                                    <td className="py-3 pr-3 font-medium text-slate-900">{row[0]}</td>
                                    <td className="py-3 pr-3 text-slate-600">{row[1]}</td>
                                    <td className="py-3 text-slate-600">{row[2]}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>

                <section className="card-surface p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-slate-900 mb-3">Choisir CV Analyse si</h2>
                    <p className="text-slate-600 leading-relaxed">{page.betterWhen}</p>
                </section>
                <section className="card-surface p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-slate-900 mb-3">Garder {page.competitor} si</h2>
                    <p className="text-slate-600 leading-relaxed">{page.betterThem}</p>
                </section>

                <p className="text-sm text-slate-500">
                    Autres comparatifs :
                    {' '}
                    {Object.entries(COMPARISONS)
                        .filter(([key]) => key !== slug)
                        .map(([key, item], i, arr) => (
                            <span key={key}>
                                <Link to={`/vs/${key}`} className="text-blue-700">{item.title}</Link>
                                {i < arr.length - 1 ? ' · ' : ''}
                            </span>
                        ))}
                </p>

                <section className="cta-band text-center">
                    <h2 className="text-xl font-semibold mb-2">Essayer CV Analyse</h2>
                    <p className="text-slate-300 text-sm mb-5">Score et 3 conseils offerts — rapport complet dès 5 €.</p>
                    <Link to="/" className="btn-primary">Voir mon score</Link>
                </section>
            </div>
        </div>
    );
};

export default Vs;
